// client/src/coms/settings/useMicMeter.js
// Peak level (dB) of the preferred microphone while `running`. Opens the device with the same processing the voice
// client uses, so the level matches what the noise gate sees. Everything (frame loop, audio graph, tracks) is
// released when `running` turns off or the component unmounts.
import { useEffect, useState } from 'react';
import { getPreferredInputDeviceId } from '../audio/audioDevicePrefs';

// announce: tell the voice client a microphone test is running ('coms-mic-meter-toggled'). During a call it then
// stops sending your microphone and plays it back to you instead (the device test). The speaking-animation test
// passes false: it only needs the level.
export default function useMicMeter(running, inputId = '', { announce = false } = {}) {
  const [db, setDb] = useState(-100);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!announce) return undefined;
    const send = (on) => {
      try {
        window.dispatchEvent(new CustomEvent('coms-mic-meter-toggled', { detail: { running: on } }));
      } catch {}
    };
    send(!!running);
    return () => {
      if (running) send(false);
    };
  }, [running, announce]);

  useEffect(() => {
    if (!running) {
      setDb(-100);
      setError('');
      return undefined;
    }

    let cancelled = false;
    let ctx = null;
    let analyser = null;
    let src = null;
    let raf = 0;
    let stream = null;

    const start = async () => {
      setError('');
      try {
        if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
          setError('This browser cannot open a microphone.');
          return;
        }
        const preferred = String(getPreferredInputDeviceId() || '').trim();
        const audio = { echoCancellation: true, noiseSuppression: true, autoGainControl: true };
        if (preferred) audio.deviceId = { exact: preferred };
        stream = await navigator.mediaDevices.getUserMedia({ audio });
        if (cancelled) return;

        const Ctx = window.AudioContext || window.webkitAudioContext;
        if (!Ctx) {
          setError('This browser cannot measure audio levels.');
          return;
        }
        ctx = new Ctx();
        analyser = ctx.createAnalyser();
        analyser.fftSize = 2048;
        analyser.smoothingTimeConstant = 0.2;
        src = ctx.createMediaStreamSource(stream);
        src.connect(analyser);
        const buf = new Float32Array(analyser.fftSize);

        const tick = () => {
          if (cancelled) return;
          try {
            analyser.getFloatTimeDomainData(buf);
            let peak = 0;
            for (let i = 0; i < buf.length; i++) {
              const v = buf[i] < 0 ? -buf[i] : buf[i];
              if (v > peak) peak = v;
            }
            const dbPeak = peak > 0 ? 20 * Math.log10(peak) : -100;
            setDb(Math.max(-100, Math.min(0, dbPeak)));
          } catch {}
          raf = requestAnimationFrame(tick);
        };
        raf = requestAnimationFrame(tick);
      } catch (e) {
        const name = e && e.name;
        setError(
          name === 'NotAllowedError'
            ? 'Microphone access is blocked. Allow it in your browser’s site settings, then try again.'
            : name === 'NotFoundError' || name === 'OverconstrainedError'
              ? 'The selected microphone is not available. Choose another one under Audio devices.'
              : String((e && e.message) || e || 'Could not start the microphone.')
        );
      }
    };

    start();

    return () => {
      cancelled = true;
      try {
        if (raf) cancelAnimationFrame(raf);
      } catch {}
      try {
        if (src) src.disconnect();
      } catch {}
      try {
        if (analyser) analyser.disconnect();
      } catch {}
      try {
        if (ctx && ctx.state !== 'closed') ctx.close();
      } catch {}
      try {
        if (stream) stream.getTracks().forEach((t) => t.stop());
      } catch {}
    };
  }, [running, inputId]);

  return { db, error };
}
